import { useTranslation } from 'react-i18next';
import {
  CATEGORY_DEFAULT_LABELS,
  CATEGORY_SELECT_OPTIONS,
  SICHERHEIT_DEFAULT_LABELS,
  SICHERHEIT_SELECT_OPTIONS,
} from '../diagnosis.utils';
import {
  ClassificationSelect,
  type DiagnosisClassificationLayout,
  FieldLabel,
  FieldLabelText,
  SeiteSelect,
} from './DiagnosisClassificationSelects.styles';

export type DiagnosisClassificationField = 'category' | 'sicherheit' | 'seite';

export interface SeiteOption {
  value: string;
  code: string;
  translationKey: 'links' | 'rechts' | 'beidseitig' | 'keine_angabe';
}

interface DiagnosisClassificationSelectsProps {
  category: string;
  sicherheit: string;
  seite: string;
  /** The summary stores sides as words, the immunization as letters; both hand in their list. */
  seiteOptions: SeiteOption[];
  layout: DiagnosisClassificationLayout;
  /** The summary lets the doctor clear a field; the immunization is always classified. */
  withPlaceholder?: boolean;
  disabled?: boolean;
  titles?: Partial<Record<DiagnosisClassificationField, string>>;
  onChange: (field: DiagnosisClassificationField, value: string) => void;
}

const optionText = (code: string, selected: boolean, meaning: string): string =>
  selected ? code : `${code} – ${meaning}`;

export const DiagnosisClassificationSelects = ({
  category,
  sicherheit,
  seite,
  seiteOptions,
  layout,
  withPlaceholder = false,
  disabled = false,
  titles,
  onChange,
}: DiagnosisClassificationSelectsProps) => {
  const { t } = useTranslation();
  const placeholder = withPlaceholder ? (
    <option value="">{t('result.diagnosisSection.selectPlaceholder')}</option>
  ) : null;

  return (
    <>
      <FieldLabel $layout={layout}>
        <FieldLabelText $layout={layout}>
          {t('result.diagnosisSection.categoryLabel')}
        </FieldLabelText>
        <ClassificationSelect
          $layout={layout}
          value={category}
          onChange={(event) => onChange('category', event.target.value)}
          title={titles?.category}
          disabled={disabled}
        >
          {placeholder}
          {CATEGORY_SELECT_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {optionText(
                option.code,
                option.value === category,
                t(`result.diagnosisSection.categoryValue.${option.translationKey}`, {
                  defaultValue: CATEGORY_DEFAULT_LABELS[option.translationKey],
                })
              )}
            </option>
          ))}
        </ClassificationSelect>
      </FieldLabel>

      <FieldLabel $layout={layout}>
        <FieldLabelText $layout={layout}>
          {t('result.diagnosisSection.sicherheitLabel')}
        </FieldLabelText>
        <ClassificationSelect
          $layout={layout}
          value={sicherheit}
          onChange={(event) => onChange('sicherheit', event.target.value)}
          title={titles?.sicherheit}
          disabled={disabled}
        >
          {placeholder}
          {SICHERHEIT_SELECT_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {optionText(
                option.code,
                option.value === sicherheit,
                t(`result.diagnosisSection.sicherheit.${option.translationKey}`, {
                  defaultValue: SICHERHEIT_DEFAULT_LABELS[option.translationKey],
                })
              )}
            </option>
          ))}
        </ClassificationSelect>
      </FieldLabel>

      <FieldLabel $layout={layout} $narrow>
        <FieldLabelText $layout={layout}>{t('result.diagnosisSection.seiteLabel')}</FieldLabelText>
        <SeiteSelect
          $layout={layout}
          value={seite}
          onChange={(event) => onChange('seite', event.target.value)}
          title={titles?.seite}
          disabled={disabled}
        >
          {placeholder}
          {seiteOptions.map((option) => (
            <option key={option.code} value={option.value}>
              {optionText(
                option.code,
                option.value === seite,
                t(`result.diagnosisSection.seite.${option.translationKey}`)
              )}
            </option>
          ))}
        </SeiteSelect>
      </FieldLabel>
    </>
  );
};
